import dayjs from 'dayjs'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useIntervalArray } from '@/composables/useIntervalArray'
import {
  DEFAULT_BOARD_DISPLAY_TIME,
  IMAGE_EXPIRY_BUFFER_SECONDS
} from '@/constants/times'
import { httpService } from '@/service/http.service'
import type { Board, BoardDto } from '@/types'
import parseImages from '@/utils/parseImages'

export const useBoardStore = defineStore('board', () => {
  const boards = ref<Board[]>([])
  const boardsBuffer = ref<Board[] | null>(null)
  const lastUpdateTime = ref<dayjs.Dayjs | null>(null)
  const currentBoardIndex = ref(0)
  const loopCount = ref(0)
  const isRefreshingBoards = ref(false)

  const boardsIntervalArray = computed(() =>
    boards.value.length > 0
      ? boards.value.map(board =>
          board.display_time
            ? board.display_time * 1000
            : DEFAULT_BOARD_DISPLAY_TIME
        )
      : [DEFAULT_BOARD_DISPLAY_TIME]
  )

  const { start: startWidgetsSlider, stop: stopWidgetsSlider } =
    useIntervalArray(boardsIntervalArray, nextBoard)

  async function fetchBoards() {
    try {
      const boardsResponse =
        (await httpService.infoboards.infoboardsPublicBoardsList()) as BoardDto[]
      const boardsWithImages = boardsResponse
        .map(board => ({
          ...board,
          widgets: board.widgets.map(widget =>
            parseImages(widget, board.layout)
          )
        }))
        .filter(
          board =>
            !(
              board.widgets[0]?.resourcetype === 'CongratulationsWidget' &&
              board.widgets[0]?.grades.length === 0
            )
        )

      if (boards.value.length === 0) boards.value = boardsWithImages
      else boardsBuffer.value = boardsWithImages

      lastUpdateTime.value = dayjs()
    } catch {
      // console.log(err)
    }
  }

  function startSlider() {
    startWidgetsSlider(currentBoardIndex.value)
  }

  function stopSlider() {
    stopWidgetsSlider()
  }

  function onLastBoard() {
    loopCount.value++
    if (boardsBuffer.value !== null) {
      boards.value = boardsBuffer.value
      boardsBuffer.value = null
    }
  }

  function getBufferedBoard(boardId: Board['id']) {
    return boardsBuffer.value?.find(board => board.id === boardId)
  }

  function collectImageExpirations(value: unknown, expirations: string[]) {
    if (!value) return
    if (Array.isArray(value)) {
      value.forEach(item => collectImageExpirations(item, expirations))
      return
    }
    if (typeof value !== 'object') return

    const maybeImage = value as {
      dataset?: { expires?: unknown }
    }
    if (
      typeof maybeImage.dataset?.expires === 'string' &&
      maybeImage.dataset.expires.length > 0
    ) {
      expirations.push(maybeImage.dataset.expires)
      return
    }

    Object.values(value).forEach(item =>
      collectImageExpirations(item, expirations)
    )
  }

  function boardHasExpiredImages(board: Board, threshold: dayjs.Dayjs) {
    const imageExpirations: string[] = []
    collectImageExpirations(board.widgets, imageExpirations)

    return imageExpirations.some(expiration => {
      const expiresAt = dayjs.unix(Number(expiration))
      return expiresAt.isValid() && !expiresAt.isAfter(threshold)
    })
  }

  function upcomingBoardsNeedRefresh() {
    const threshold = dayjs().add(IMAGE_EXPIRY_BUFFER_SECONDS, 'second')

    for (let offset = 1; offset < boards.value.length; offset++) {
      const boardIndex =
        (currentBoardIndex.value + offset) % boards.value.length
      const currentBoard = boards.value[boardIndex]
      if (!currentBoard) continue
      const preferredBoard = getBufferedBoard(currentBoard.id) ?? currentBoard

      if (boardHasExpiredImages(preferredBoard, threshold)) {
        return true
      }
    }

    return false
  }

  async function refreshUpcomingBoards() {
    if (isRefreshingBoards.value) return
    if (!upcomingBoardsNeedRefresh()) return

    isRefreshingBoards.value = true
    try {
      await fetchBoards()
    } finally {
      isRefreshingBoards.value = false
    }
  }

  function nextBoard() {
    if (boards.value.length === 0) return
    const isItLastOne = boards.value.length - 1 === currentBoardIndex.value
    currentBoardIndex.value = isItLastOne ? 0 : currentBoardIndex.value + 1
    if (isItLastOne) {
      onLastBoard()
    }
    refreshUpcomingBoards()
  }

  function prevBoard() {
    const isItFirst = currentBoardIndex.value === 0
    currentBoardIndex.value = isItFirst
      ? boards.value.length
        ? boards.value.length - 1
        : 0
      : currentBoardIndex.value - 1
    if (isItFirst && loopCount.value > 0) loopCount.value--
  }

  const board = computed(() => {
    const currentBoard = boards.value[currentBoardIndex.value]
    if (!currentBoard) return currentBoard

    return getBufferedBoard(currentBoard.id) ?? currentBoard
  })

  const boardLayout = computed(() => board.value?.layout)

  const sliderColors = computed(() =>
    boards.value.map(board => {
      const color = board.label_color || board.widgets[0]?.bg_color
      return {
        color: color || '#808080',
        id: board.id
      }
    })
  )

  return {
    loopCount,
    sliderColors,
    board,
    boards,
    currentBoardIndex,
    boardLayout,
    fetchBoards,
    lastUpdateTime,
    nextBoard,
    prevBoard,
    startSlider,
    stopSlider
  }
})
